import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { ArrowLeft, Info, Cpu, HeartHandshake, Filter, FileText, UserPlus } from "lucide-react";
import { toast } from "sonner";

// Order in which function groups are rendered
const FUNCTION_ORDER = [
  "Comment", "Request", "Protest", "Describe", "Express feelings",
  "Sharing information", "Direct action", "Open invitation", "Regulation",
  "Repair", "Comprehension check", "Choice-making", "Greeting",
];

export default function Results() {
  const { id } = useParams();
  const { authHeaders, user, openAuth } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/history/${id}`, { headers: authHeaders() });
        setData(res.data);
      } catch {
        toast.error("Could not load results");
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  // Group rows by communicative_function, preserving first-seen order
  const grouped = useMemo(() => {
    if (!data) return [];
    const seen = new Map(); // fn -> array
    for (const r of data.rows) {
      const fn = (r.communicative_function || "Other").trim() || "Other";
      if (!seen.has(fn)) seen.set(fn, []);
      seen.get(fn).push(r);
    }
    // Sort using FUNCTION_ORDER first, then any remaining keys
    const known = FUNCTION_ORDER.filter((k) => seen.has(k));
    const extras = [...seen.keys()].filter((k) => !FUNCTION_ORDER.includes(k));
    return [...known, ...extras].map((fn) => ({ fn, items: seen.get(fn) }));
  }, [data]);

  if (!data) {
    return <div className="max-w-4xl mx-auto px-6 py-20 text-[#52606D]" data-testid="results-loading">Loading…</div>;
  }

  const mode = data.mode || "standard";
  const isGestaltBank = mode === "gestalt_unit_bank";
  const hideOneWord = mode === "two_three_word_only";
  const hideTwoThree = isGestaltBank;

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="results-page">
      <Link to="/search" className="inline-flex items-center gap-2 text-sm text-[#2C4C3B] hover:underline mb-6" data-testid="results-back-link">
        <ArrowLeft className="w-4 h-4" /> New search
      </Link>

      {user?.is_guest && (
        <div className="mb-6 p-5 rounded-2xl bg-[#FDF6F0] border border-[#C66A51]/30 flex items-start gap-3 flex-wrap" data-testid="guest-upsell-banner">
          <UserPlus className="w-5 h-5 text-[#C66A51] mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-[260px]">
            <p className="text-sm text-[#2C4C3B] font-medium">This list is saved to a guest session.</p>
            <p className="text-xs text-[#52606D] mt-0.5">Sign up free (under 30s) to save your history, generate more lists, and unlock the SMART goal bank.</p>
          </div>
          <button
            onClick={() => openAuth({ mode: "signup", reason: "Sign up to keep this list in your history." })}
            className="px-4 py-2 rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white text-sm font-medium"
            data-testid="guest-upsell-cta">
            Save to my account
          </button>
        </div>
      )}

      {data.is_trial && (
        <div className="mb-6 p-5 rounded-2xl bg-[#2C4C3B] text-white flex items-start gap-3 flex-wrap" data-testid="trial-banner">
          <div className="flex-1 min-w-[260px]">
            <div className="text-xs tracking-[0.2em] uppercase text-[#A8C9AC]">Free trial preview</div>
            <p className="text-sm mt-1">You're viewing the first <strong>{data.trial_truncated_at || 15} rows</strong> of {data.disorder_name} programming for {data.activity_name}. Subscribe to unlock the full 50-row table and complete core / fringe word banks.</p>
          </div>
          <Link to="/plans" className="px-5 py-2.5 rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white text-sm font-semibold" data-testid="trial-banner-subscribe">
            Subscribe to unlock →
          </Link>
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]" data-testid="results-context-label">
            {data.disorder_name} · {data.communicator_stage_title} · {data.severity_label}
            {data.user_age_band && <> · {data.user_age_band === "adult" ? "Adult" : "Child"}</>}
            {data.location_label && <> · {data.location_label}</>}
          </span>
          <h1 className="font-display text-4xl font-semibold text-[#2C4C3B] mt-2" data-testid="results-heading">
            {data.activity_name}
          </h1>
          <p className="text-[#52606D] mt-1 text-sm">
            Device: <span className="font-medium text-[#2C4C3B]">{data.device === "avaz" ? "Avaz AAC" : "Generic AAC"}</span>
          </p>
        </div>
      </div>

      {/* Ratio panel */}
      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-2xl bg-white border border-[#E2E8F0]">
          <div className="text-xs tracking-[0.15em] uppercase text-[#52606D]">Rows generated</div>
          <div className="font-display text-3xl text-[#2C4C3B] mt-1" data-testid="stat-rows">{data.rows.length}</div>
        </div>
        <div className="p-5 rounded-2xl core-badge border border-[#2C4C3B]/10">
          <div className="text-xs tracking-[0.15em] uppercase">Core ratio</div>
          <div className="font-display text-3xl mt-1" data-testid="stat-ratio-core">{data.ratio_core}%</div>
          <div className="text-xs mt-1">{data.core_words.length} core words</div>
        </div>
        <div className="p-5 rounded-2xl fringe-badge border border-[#C66A51]/20">
          <div className="text-xs tracking-[0.15em] uppercase">Fringe ratio</div>
          <div className="font-display text-3xl mt-1" data-testid="stat-ratio-fringe">{data.ratio_fringe}%</div>
          <div className="text-xs mt-1">{data.fringe_words.length} fringe words</div>
        </div>
      </div>

      {/* Avaz panel */}
      {data.device === "avaz" && data.avaz_notes && (
        <div className="mt-8 p-6 rounded-2xl bg-[#2C4C3B] text-white" data-testid="avaz-panel">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase opacity-80">
            <Cpu className="w-4 h-4" /> Avaz device tips
          </div>
          <p className="mt-2 leading-relaxed text-sm opacity-95 whitespace-pre-wrap">{data.avaz_notes}</p>
        </div>
      )}

      {/* Target sound filter banner (articulation only) */}
      {data.target_sound_filter && (
        <div className="mt-6 p-4 rounded-2xl bg-[#FDF6F0] border border-[#C66A51]/30 flex items-center gap-3" data-testid="target-sound-banner">
          <Filter className="w-4 h-4 text-[#C66A51]" />
          <p className="text-sm text-[#2C4C3B] font-medium">{data.target_sound_filter}</p>
        </div>
      )}

      {/* Disorder-specific clinical note */}
      {data.clinical_note && (
        <div className="mt-4 p-5 rounded-2xl bg-white border border-[#2C4C3B]/15 flex items-start gap-3" data-testid="clinical-note-panel">
          <FileText className="w-5 h-5 text-[#2C4C3B] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#1A202C] leading-relaxed"><span className="font-semibold">Clinical note:</span> {data.clinical_note}</p>
        </div>
      )}

      {/* Clinical Table */}
      <section className="mt-10">
        <h2 className="font-display text-2xl text-[#2C4C3B] mb-4">
          {isGestaltBank ? "Gestalt unit bank" : "Programming table"}
        </h2>

        {/* Neurodiversity-affirming disclaimer */}
        <div className="mb-5 p-5 rounded-2xl bg-[#E6F0E9] border border-[#2C4C3B]/15 flex items-start gap-3" data-testid="results-disclaimer">
          <HeartHandshake className="w-5 h-5 text-[#2C4C3B] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#2C4C3B] leading-relaxed">
            <span className="font-semibold">The examples below are for reference only.</span> Always use individual-led, neurodiversity-affirming language —
            prioritise comments over questions, follow the person&rsquo;s interest, and describe rather than test. Avoid prompts like
            <em> &ldquo;Say hi&rdquo;</em> or <em>&ldquo;Tell me what this is.&rdquo;</em> Adapt to the person&rsquo;s age, stage, and support needs.
            <span className="block mt-1 font-medium">Connection first. Communication always.</span>
          </p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-[#E2E8F0] bg-white" data-testid="clinical-table-wrap">
          <table className="w-full text-sm" data-testid="clinical-table">
            <thead>
              <tr className="bg-[#F4F5F0] text-left">
                <th className="px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase text-[#52606D]">Activity</th>
                <th className="px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase text-[#52606D]">
                  {isGestaltBank ? "Meaning / when used" : "What to say"}
                </th>
                <th className="px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase text-[#52606D]">Function</th>
                {!hideOneWord && (
                  <th className="px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase text-[#52606D]">
                    {isGestaltBank ? "Gestalt unit" : "1-Word"}
                  </th>
                )}
                {!hideTwoThree && (
                  <>
                    <th className="px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase text-[#52606D]">2-Word</th>
                    <th className="px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase text-[#52606D]">3-Word</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {grouped.map((group, gi) => (
                <React.Fragment key={group.fn}>
                  {!isGestaltBank && (
                    <tr className="bg-[#FDFCF8] border-t border-[#E2E8F0]" data-testid={`function-group-header-${group.fn.toLowerCase().replace(/\s+/g, '-')}`}>
                      <td colSpan={6 - (hideOneWord ? 1 : 0) - (hideTwoThree ? 2 : 0)} className="px-4 py-2 text-xs tracking-[0.15em] uppercase text-[#C66A51] font-semibold">
                        {group.fn} <span className="text-[#52606D] normal-case tracking-normal">· {group.items.length} {group.items.length === 1 ? "row" : "rows"}</span>
                      </td>
                    </tr>
                  )}
                  {group.items.map((r, i) => (
                    <tr key={`${gi}-${i}`} className="border-t border-[#E2E8F0] hover:bg-[#FDFCF8] fade-up"
                        style={{ animationDelay: `${i * 15}ms` }}
                        data-testid={`clinical-row-${gi}-${i}`}>
                      <td className="px-4 py-3 text-[#52606D]">{data.activity_name}</td>
                      <td className="px-4 py-3 text-[#1A202C]">{r.what_to_say}</td>
                      <td className="px-4 py-3">
                        <span className="px-2.5 py-1 rounded-full text-xs core-badge">{r.communicative_function}</span>
                      </td>
                      {!hideOneWord && <td className="px-4 py-3 font-medium text-[#2C4C3B]">{r.one_word}</td>}
                      {!hideTwoThree && (
                        <>
                          <td className="px-4 py-3 text-[#2C4C3B]">{r.two_word}</td>
                          <td className="px-4 py-3 text-[#2C4C3B]">{r.three_word}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {data.is_trial && !isGestaltBank && (
            <div className="border-t border-[#E2E8F0] bg-[#FDF6F0] px-5 py-4 flex items-center justify-between flex-wrap gap-3" data-testid="table-unlock-overlay">
              <div className="text-sm text-[#2C4C3B]">
                <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51] block mb-1">35+ more rows locked</span>
                Subscribe to see the full programming table with every communicative function fully populated.
              </div>
              <Link to="/plans" className="px-5 py-2 rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white text-sm font-semibold" data-testid="table-unlock-cta">
                Unlock the rest →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Word banks */}
      {!isGestaltBank && (
        <section className="mt-10 grid lg:grid-cols-2 gap-6 relative">
          <div className="p-6 rounded-2xl bg-white border border-[#E2E8F0]" data-testid="core-bank">
            <h3 className="font-display text-lg text-[#2C4C3B]">Core word bank ({data.ratio_core}%)</h3>
            <p className="text-xs text-[#52606D] mt-1">High-frequency, reusable across contexts.</p>
            {data.target_sound_filter && (
              <p className="text-xs text-[#C66A51] mt-1 font-medium">{data.target_sound_filter}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {data.core_words.map((w, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-sm core-badge" data-testid={`core-word-${i}`}>{w}</span>
              ))}
              {data.is_trial && (
                <span className="px-3 py-1 rounded-full text-sm bg-[#F4F5F0] text-[#52606D] border border-dashed border-[#C66A51]/40">+ many more</span>
              )}
            </div>
          </div>
          <div className="p-6 rounded-2xl bg-white border border-[#E2E8F0]" data-testid="fringe-bank">
            <h3 className="font-display text-lg text-[#2C4C3B]">Fringe word bank ({data.ratio_fringe}%)</h3>
            <p className="text-xs text-[#52606D] mt-1">Activity-specific labels &amp; motivating items.</p>
            {data.target_sound_filter && (
              <p className="text-xs text-[#C66A51] mt-1 font-medium">{data.target_sound_filter}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {data.fringe_words.map((w, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-sm fringe-badge" data-testid={`fringe-word-${i}`}>{w}</span>
              ))}
              {data.is_trial && (
                <span className="px-3 py-1 rounded-full text-sm bg-[#F4F5F0] text-[#52606D] border border-dashed border-[#C66A51]/40">+ many more</span>
              )}
            </div>
          </div>

          {data.is_trial && (
            <div className="lg:col-span-2 -mt-2 p-6 rounded-2xl border-2 border-dashed border-[#C66A51]/40 bg-[#FDF6F0] flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="banks-unlock-overlay">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Locked</div>
                <p className="text-sm text-[#2C4C3B] mt-1">The full <strong>25-35 core</strong> + <strong>10-20 fringe</strong> bank — culturally adapted, target-sound filtered — unlocks with a CommPass subscription.</p>
              </div>
              <Link to="/plans" className="px-5 py-2.5 rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white text-sm font-semibold" data-testid="banks-unlock-cta">
                See plans →
              </Link>
            </div>
          )}
        </section>
      )}

      <div className="mt-10 p-5 rounded-2xl bg-[#F4F5F0] border border-[#E2E8F0] flex items-start gap-3" data-testid="clinical-notes-cta">
        <Info className="w-5 h-5 text-[#2C4C3B] mt-0.5" />
        <div className="text-sm text-[#52606D]">
          Need a refresher on modelling, partner skills, or attributing meaning?{" "}
          <Link to="/clinical-notes" className="text-[#2C4C3B] font-medium hover:underline">Open Clinical Notes →</Link>
        </div>
      </div>
    </div>
  );
}
